<template>
  <div class="vocabulary-book">
    <header class="workspace-header">
      <div class="workspace-heading">
        <h1 class="workspace-title">{{ copy.title }}</h1>
        <p class="workspace-subtitle">{{ copy.subtitle }}</p>
      </div>
      <div class="notebook-tabs" :class="{ 'is-second': activeTab === 'sentences' }" role="tablist" :aria-label="copy.tabLabel">
        <button type="button" role="tab" :aria-selected="activeTab === 'words'" :class="{ active: activeTab === 'words' }" @click="activeTab = 'words'">
          {{ copy.wordsTab }}
        </button>
        <button type="button" role="tab" :aria-selected="activeTab === 'sentences'" :class="{ active: activeTab === 'sentences' }" @click="activeTab = 'sentences'">
          {{ copy.sentencesTab }}
        </button>
      </div>
    </header>

    <section v-if="activeTab === 'words'" class="vocabulary-panel">
      <div v-if="loading" class="state-panel">
        {{ copy.loading }}
      </div>
      <div v-else-if="error" class="state-panel error">
        <span>{{ error }}</span>
        <button type="button" @click="loadEntries">{{ copy.retry }}</button>
      </div>
      <div v-else-if="entries.length === 0" class="state-panel">
        {{ copy.empty }}
      </div>

      <div
        v-else
        class="vocabulary-content"
      >
        <section class="word-library" :aria-label="copy.libraryTitle">
          <div class="library-head">
            <label class="vocabulary-search">
              <span class="material-icons" aria-hidden="true">search</span>
              <input
                v-model.trim="search"
                type="search"
                :placeholder="copy.searchPlaceholder"
              >
            </label>
            <span class="library-count">{{ copy.count(filteredEntries.length) }}</span>
            <button
              type="button"
              class="export-button"
              @click="exportVocabulary"
            >
              {{ copy.export }}
            </button>
          </div>

          <p v-if="filteredEntries.length === 0" class="library-empty">{{ copy.noSearchResult }}</p>
          <template v-else>
            <div class="word-table-head" aria-hidden="true">
              <span>{{ copy.columns.word }}</span>
              <span>{{ copy.columns.meaning }}</span>
              <span class="col-context">{{ copy.columns.context }}</span>
              <span class="col-date">{{ copy.columns.saved }}</span>
              <span></span>
            </div>
            <div class="word-list">
              <article
                v-for="entry in filteredEntries"
                :key="entry.id || entry.word"
                class="word-row"
                :class="{ selected: expandedEntryId === getEntryId(entry) }"
                role="button"
                tabindex="0"
                @click="toggleEntry(entry)"
                @keydown.enter="toggleEntry(entry)"
                @keydown.space.prevent="toggleEntry(entry)"
              >
                <span class="col-word">
                  <strong class="word-text">{{ entry.displayWord || entry.word }}</strong>
                  <span v-if="displayPhonetic(entry.phonetic)" class="phonetic">{{ displayPhonetic(entry.phonetic) }}</span>
                </span>
                <span class="col-meaning">{{ entry.translation || copy.noTranslation }}</span>
                <span class="col-context">{{ entry.lastContext }}</span>
                <span class="col-date">{{ formatShortDate(entry.updatedAt) }}</span>
                <button
                  v-if="entry.word || entry.displayWord"
                  type="button"
                  class="row-audio"
                  :title="copy.listen"
                  :aria-label="copy.listen"
                  @click.stop="playAudio(entry.audioUrl, entry.displayWord || entry.word)"
                >
                  <span class="icon-sound"></span>
                </button>
              </article>
            </div>
          </template>
        </section>

        <aside class="word-detail-panel">
          <template v-if="selectedEntry">
            <header class="detail-head">
              <div class="detail-heading">
                <strong class="detail-word">{{ selectedEntry.displayWord || selectedEntry.word }}</strong>
                <div class="detail-sub">
                  <span v-if="displayPhonetic(selectedEntry.phonetic)" class="phonetic">
                    {{ displayPhonetic(selectedEntry.phonetic) }}
                  </span>
                  <span v-if="selectedEntry.partOfSpeech" class="detail-pos">{{ selectedEntry.partOfSpeech }}</span>
                  <span v-if="showLemma(selectedEntry)" class="detail-lemma">{{ copy.baseForm }} {{ selectedEntry.lemma }}</span>
                </div>
              </div>
              <button
                v-if="selectedEntry.word || selectedEntry.displayWord"
                type="button"
                class="detail-play"
                :title="copy.listen"
                :aria-label="copy.listen"
                @click="playAudio(selectedEntry.audioUrl, selectedEntry.displayWord || selectedEntry.word)"
              >
                <span class="icon-sound"></span>
              </button>
            </header>

            <p class="detail-translation">{{ selectedEntry.translation || copy.noTranslation }}</p>
            <p v-if="selectedEntry.contextMeaning" class="detail-context-meaning">{{ selectedEntry.contextMeaning }}</p>

            <section v-if="selectedEntry.lastContext || canOpen(selectedEntry)" class="detail-section">
              <h3>{{ copy.context }}</h3>
              <p v-if="selectedEntry.lastContext" class="context-quote">
                <template v-for="(part, index) in contextParts(selectedEntry)" :key="index">
                  <mark v-if="part.hit">{{ part.text }}</mark>
                  <template v-else>{{ part.text }}</template>
                </template>
              </p>
              <div v-if="canOpen(selectedEntry)" class="source-row">
                <span class="source-name">{{ sourceLabel(selectedEntry) }}</span>
                <button type="button" class="open-source" :disabled="opening" @click="openSource(selectedEntry)">
                  {{ opening ? copy.opening : copy.openSource }}
                  <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8h8.5M8.5 4.5L12 8l-3.5 3.5" /></svg>
                </button>
              </div>
              <p v-if="actionError" class="action-error">{{ actionError }}</p>
            </section>

            <section v-if="selectedEntry.definitions?.length" class="detail-section">
              <h3>{{ copy.definitions }}</h3>
              <ol class="definitions-list">
                <li
                  v-for="(definition, index) in selectedEntry.definitions"
                  :key="`${getEntryId(selectedEntry)}-${index}`"
                >
                  <span v-if="definition.partOfSpeech" class="definition-pos">{{ definition.partOfSpeech }}</span>
                  {{ definition.definition }}
                </li>
              </ol>
            </section>

            <footer class="detail-footer">
              <span>{{ copy.savedAt }} {{ formatDate(selectedEntry.updatedAt) || copy.recentlySaved }}</span>
              <button
                type="button"
                class="detail-delete"
                :disabled="deleting"
                @click="deleteEntry(selectedEntry)"
              >
                {{ deleting ? copy.deleting : copy.delete }}
              </button>
            </footer>
          </template>

          <div v-else class="detail-empty">
            <div class="detail-empty-mark">Aa</div>
            <strong>{{ copy.detailEmptyTitle }}</strong>
            <p>{{ copy.detailEmptyText }}</p>
          </div>
        </aside>
      </div>
    </section>

    <SentenceNotebook v-else />

    <DialogModal
      v-if="showDeleteDialog"
      :show="showDeleteDialog"
      :title="copy.deleteDialogTitle"
      @close="closeDeleteDialog"
    >
      <p class="delete-message">
        {{ copy.deleteDialogMessage(selectedDeleteEntry?.displayWord || selectedDeleteEntry?.word || '') }}
      </p>
      <p class="delete-warning">{{ copy.deleteDialogWarning }}</p>
      <template #buttons>
        <button class="dialog-action secondary" type="button" @click="closeDeleteDialog">
          {{ copy.cancel }}
        </button>
        <button
          class="dialog-action danger"
          type="button"
          :disabled="deleting"
          @click="confirmDeleteEntry"
        >
          {{ deleting ? copy.deleting : copy.confirmDelete }}
        </button>
      </template>
    </DialogModal>
  </div>
</template>

<script>
import { apiPost } from '@/composables/useApi'
import { auth } from '@/firebase'
import DialogModal from '@/components/modals/DialogModal.vue'
import SentenceNotebook from '@/components/learning/SentenceNotebook.vue'
import { useViews } from '@/composables/views'
import { playVocabularyAudio } from '@/utils/vocabularyAudio'
import { invalidateSavedVocabulary } from '@/composables/useSavedVocabulary'
import {
  canOpenStudySource,
  resolveStudySource,
  StudySourceMissingError,
  StudySourceNotReadyError,
} from '@/composables/studySource'

const copyByLocale = {
  zh: {
    title: '学习本',
    subtitle: '集中复习你保存的单词和 AI 单句解释。',
    tabLabel: '学习本分类',
    wordsTab: '单词',
    sentencesTab: '句子',
    loading: '正在加载单词本...',
    retry: '重试',
    empty: '还没有加入单词。到字幕页点击单词，再按“加入单词本”。',
    noSearchResult: '没有匹配的单词。',
    searchPlaceholder: '搜索单词或释义',
    noTranslation: '暂无翻译',
    listen: '发音',
    close: '关闭',
    export: '导出',
    delete: '删除单词',
    deleting: '删除中...',
    cancel: '取消',
    confirmDelete: '删除',
    deleteDialogTitle: '删除单词',
    deleteDialogMessage: (word) => `确定删除「${word}」吗？`,
    deleteDialogWarning: '删除后不会影响原字幕内容。',
    detailTitle: '词条详情',
    studyCard: '当前词条',
    libraryTitle: '复习队列',
    libraryHint: '按最近保存排序。',
    savedAt: '保存于',
    baseForm: '原形',
    columns: { word: '单词', meaning: '释义', context: '出处', saved: '保存' },
    recentlySaved: '刚刚',
    definitions: '其他义项',
    context: '原句',
    noDetails: '这个词暂时没有更多释义。',
    detailEmptyTitle: '选择一个单词',
    detailEmptyText: '在左侧点选单词后，这里会显示释义和上下文。',
    loginRequired: '请先登录再查看单词本。',
    count: (count) => `${count} 个单词`,
    openSource: '回到原文',
    opening: '正在打开',
    sourceFrom: (name) => `来自《${name}》`,
    sourceUnknown: '来自你的练习',
    sourceMissing: '原文已被删除，无法打开。',
    openFailed: '暂时无法打开原文，请稍后重试。',
  },
  en: {
    title: 'Study notebook',
    subtitle: 'Review saved words and AI sentence explanations in one place.',
    tabLabel: 'Study notebook sections',
    wordsTab: 'Words',
    sentencesTab: 'Sentences',
    loading: 'Loading vocabulary...',
    retry: 'Retry',
    empty: 'No saved words yet. Click a word in subtitles, then add it here.',
    noSearchResult: 'No matching words.',
    searchPlaceholder: 'Search words or translations',
    noTranslation: 'No translation',
    listen: 'Listen',
    close: 'Close',
    export: 'Export',
    delete: 'Delete word',
    deleting: 'Deleting...',
    cancel: 'Cancel',
    confirmDelete: 'Delete',
    deleteDialogTitle: 'Delete word',
    deleteDialogMessage: (word) => `Delete "${word}"?`,
    deleteDialogWarning: 'This will not change the original subtitles.',
    detailTitle: 'Word detail',
    studyCard: 'Current entry',
    libraryTitle: 'Review queue',
    libraryHint: 'Sorted by recently saved words.',
    savedAt: 'Saved',
    baseForm: 'Base form',
    columns: { word: 'Word', meaning: 'Meaning', context: 'Source', saved: 'Saved' },
    recentlySaved: 'Recently',
    definitions: 'Other meanings',
    context: 'Original sentence',
    noDetails: 'No extra definitions yet.',
    detailEmptyTitle: 'Choose a word',
    detailEmptyText: 'Select a word on the left to review definitions and context.',
    loginRequired: 'Please log in to view vocabulary.',
    count: (count) => `${count} words`,
    openSource: 'Back to source',
    opening: 'Opening',
    sourceFrom: (name) => `From “${name}”`,
    sourceUnknown: 'From your practice',
    sourceMissing: 'The source has been deleted and can’t be opened.',
    openFailed: 'Couldn’t open the source. Try again later.',
  },
}

export default {
  name: 'VocabularyBook',
  components: {
    DialogModal,
    SentenceNotebook,
  },
  setup() {
    const { handleViewChange } = useViews()
    return { _handleViewChange: handleViewChange }
  },
  data() {
    return {
      loading: false,
      activeTab: useViews().contentProps.value.notebookTab || 'words',
      error: '',
      entries: [],
      search: '',
      expandedEntryId: '',
      deleting: false,
      showDeleteDialog: false,
      selectedDeleteEntry: null,
      refreshingEntryIds: new Set(),
      opening: false,
      actionError: '',
    }
  },
  computed: {
    copy() {
      return this.$i18n?.locale === 'en' ? copyByLocale.en : copyByLocale.zh
    },
    filteredEntries() {
      const keyword = this.search.toLowerCase()
      if (!keyword) return this.entries
      return this.entries.filter((entry) => {
        return [
          entry.word,
          entry.displayWord,
          entry.translation,
          ...(entry.translations || []).map((item) => item.text),
        ].some((value) => String(value || '').toLowerCase().includes(keyword))
      })
    },
    selectedEntry() {
      if (!this.expandedEntryId) return null
      return this.filteredEntries.find((entry) => this.getEntryId(entry) === this.expandedEntryId) || null
    },
  },
  watch: {
    filteredEntries(entries) {
      if (!entries.length) {
        this.expandedEntryId = ''
        return
      }

      const selectedVisible = entries.some((entry) => this.getEntryId(entry) === this.expandedEntryId)
      if (!selectedVisible) {
        this.expandedEntryId = this.getEntryId(entries[0])
      }
    },
  },
  mounted() {
    this.loadEntries()
  },
  methods: {
    async loadEntries() {
      if (!auth.currentUser) {
        this.error = this.copy.loginRequired
        this.entries = []
        return
      }

      this.loading = true
      this.error = ''
      try {
        const result = await apiPost('/api/vocabulary/list', { limit: 200 })
        this.entries = result.entries || []
        this.expandedEntryId = this.entries.length ? this.getEntryId(this.entries[0]) : ''
        if (this.entries[0]) this.ensureEntryDetails(this.entries[0])
        this.refreshMissingPhonetics()
      } catch (error) {
        this.error = error?.message || 'Failed to load vocabulary'
      } finally {
        this.loading = false
      }
    },
    getEntryId(entry) {
      if (!entry) return ''
      return String(entry.id || entry.word || entry.displayWord || '')
    },
    entrySource(entry) {
      return {
        audioId: entry.lastAudioId,
        audioName: entry.lastAudioName,
        materialId: entry.lastMaterialId,
        materialBook: entry.lastMaterialBook,
      }
    },
    canOpen(entry) {
      return canOpenStudySource(this.entrySource(entry))
    },
    sourceLabel(entry) {
      return entry.lastAudioName ? this.copy.sourceFrom(entry.lastAudioName) : this.copy.sourceUnknown
    },
    async openSource(entry) {
      if (this.opening) return
      this.opening = true
      this.actionError = ''
      const translate = (...args) => this.$t(...args)
      translate.locale = this.$i18n?.locale
      try {
        const audio = await resolveStudySource(this.entrySource(entry), translate)
        this._handleViewChange('AudioPlayer', {
          ...audio,
          useSmartSplit: false,
          isDemo: false,
          focusSentence: { text: entry.lastContext, index: entry.lastSubtitleIndex },
          returnView: 'VocabularyBook',
          returnParams: { notebookTab: 'words' },
        })
      } catch (error) {
        if (error instanceof StudySourceMissingError) this.actionError = this.copy.sourceMissing
        else if (error instanceof StudySourceNotReadyError) this.actionError = this.copy.openFailed
        else this.actionError = error?.message || this.copy.openFailed
      } finally {
        this.opening = false
      }
    },
    toggleEntry(entry) {
      this.actionError = ''
      this.expandedEntryId = this.getEntryId(entry)
      this.ensureEntryDetails(entry)
    },
    async ensureEntryDetails(entry) {
      const entryId = this.getEntryId(entry)
      const isComplete = entry.translation
        && entry.definitions?.length
        && entry.phonetic
        && !this.hasLegacyPhonetic(entry.phonetic)
      if (!entryId || isComplete || this.refreshingEntryIds.has(entryId)) {
        return
      }

      this.refreshingEntryIds.add(entryId)
      try {
        const result = await apiPost('/api/vocabulary/lookup', {
          word: entry.displayWord || entry.word,
          context: entry.lastContext || '',
          sourceLanguage: entry.sourceLanguage || 'en',
          targetLanguage: entry.targetLanguage || 'zh',
        })
        const index = this.entries.findIndex((item) => this.getEntryId(item) === entryId)
        if (index >= 0) {
          this.entries.splice(index, 1, { ...this.entries[index], ...result, id: entryId })
        }
      } catch (error) {
        console.warn('Vocabulary enrichment failed:', error)
      } finally {
        this.refreshingEntryIds.delete(entryId)
      }
    },
    hasLegacyPhonetic(value) {
      return /\b[A-Z]{1,4}[012]\b/.test(String(value || ''))
    },
    displayPhonetic(value) {
      return this.hasLegacyPhonetic(value) ? '' : String(value || '')
    },
    async refreshMissingPhonetics() {
      const entries = this.entries
        .filter((entry) => !entry.phonetic || this.hasLegacyPhonetic(entry.phonetic))
        .slice(0, 12)
      await Promise.all(entries.map((entry) => this.ensureEntryDetails(entry)))
    },
    deleteEntry(entry) {
      this.selectedDeleteEntry = entry
      this.showDeleteDialog = true
    },
    closeDeleteDialog() {
      if (this.deleting) return
      this.showDeleteDialog = false
      this.selectedDeleteEntry = null
    },
    async confirmDeleteEntry() {
      const entry = this.selectedDeleteEntry
      const entryId = this.getEntryId(entry)
      if (!entryId || this.deleting) return

      this.deleting = true
      try {
        const visibleEntries = [...this.filteredEntries]
        const currentIndex = visibleEntries.findIndex((item) => this.getEntryId(item) === entryId)
        await apiPost('/api/vocabulary/delete', { id: entryId })
        invalidateSavedVocabulary()
        this.entries = this.entries.filter((item) => this.getEntryId(item) !== entryId)
        const nextEntry = visibleEntries[currentIndex + 1] || visibleEntries[currentIndex - 1] || null
        this.expandedEntryId = nextEntry ? this.getEntryId(nextEntry) : ''
        this.showDeleteDialog = false
        this.selectedDeleteEntry = null
      } catch (error) {
        this.error = error?.message || 'Failed to delete vocabulary'
      } finally {
        this.deleting = false
      }
    },
    exportVocabulary() {
      const rows = this.entries.map((entry) => ({
        word: entry.displayWord || entry.word || '',
        phonetic: entry.phonetic || '',
        translation: entry.translation || '',
        definitions: (entry.definitions || [])
          .map((item) => [item.partOfSpeech, item.definition].filter(Boolean).join(' '))
          .join('\n'),
        context: entry.lastContext || '',
        updatedAt: this.formatDate(entry.updatedAt),
      }))
      if (!rows.length) return

      const headers = ['word', 'phonetic', 'translation', 'definitions', 'context', 'updatedAt']
      const csv = [
        headers.join(','),
        ...rows.map((row) => headers.map((key) => this.escapeCsv(row[key])).join(',')),
      ].join('\r\n')
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `goodscriber-vocabulary-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    escapeCsv(value) {
      return `"${String(value || '').replace(/"/g, '""')}"`
    },
    async playAudio(url, word) {
      try {
        await playVocabularyAudio(url, word)
      } catch (error) {
        console.warn('Pronunciation playback failed:', error)
      }
    },
    showLemma(entry) {
      const lemma = String(entry.lemma || '').trim().toLowerCase()
      return Boolean(lemma) && lemma !== String(entry.displayWord || entry.word || '').trim().toLowerCase()
    },
    contextParts(entry) {
      const text = String(entry.lastContext || '')
      const word = String(entry.displayWord || entry.word || '').trim()
      if (!word) return [{ text, hit: false }]
      const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return text
        .split(new RegExp(`(\\b${escaped}\\b)`, 'gi'))
        .filter(Boolean)
        .map((part) => ({ text: part, hit: part.toLowerCase() === word.toLowerCase() }))
    },
    formatShortDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return `${date.getMonth() + 1}/${date.getDate()}`
    },
    formatDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString()
    },
  },
}
</script>

<style scoped>
/*
 * 学习本·单词：一块面板，左侧对齐列的单词表 + 右侧单词卡。
 * 列表用整行分隔线，列对齐便于扫读；详情把原句里的单词标出来。
 */
.vocabulary-book {
  width: 100%;
  display: flex;
  flex-direction: column;
  color: var(--ink-primary);
}

/* 单词 / 句子切换 */
.notebook-tabs {
  position: relative;
  flex: 0 0 auto;
  padding: 2px;
  display: grid;
  grid-template-columns: repeat(2, 76px);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-subtle);
}

.notebook-tabs::before {
  content: '';
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 2px;
  width: 76px;
  border-radius: var(--radius-sm);
  background: var(--surface-panel);
  box-shadow: var(--shadow-sm);
  transition: transform 240ms ease;
}

.notebook-tabs.is-second::before {
  transform: translateX(100%);
}

.notebook-tabs button {
  position: relative;
  min-height: 30px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.notebook-tabs button.active {
  color: var(--ink-primary);
  font-weight: 600;
}

.notebook-tabs button:focus-visible {
  outline: none;
  border-radius: var(--radius-sm);
  box-shadow: var(--focus-ring-brand-strong);
}

.vocabulary-panel {
  height: var(--workspace-fill-height);
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.state-panel {
  flex: 1;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-8);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  text-align: center;
}

.state-panel.error {
  flex-direction: column;
  color: var(--color-danger);
}

.state-panel button {
  height: 32px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  cursor: pointer;
}

/* 主面板 */
.vocabulary-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 360px);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
}

.word-library {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  container-type: inline-size;
}

/* 标题栏：搜索 + 数量 + 导出 */
.library-head {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}

.vocabulary-search {
  flex: 1;
  max-width: 280px;
  height: 32px;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
}

.vocabulary-search:focus-within {
  border-color: var(--signal-active-strong);
  box-shadow: var(--focus-ring-brand);
}

.vocabulary-search .material-icons {
  color: var(--ink-tertiary);
  font-size: 16px;
}

.vocabulary-search input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
}

.vocabulary-search input::placeholder {
  color: var(--ink-tertiary);
}

.library-count {
  margin-left: auto;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  white-space: nowrap;
}

.export-button {
  height: 32px;
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.export-button:hover {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.export-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.library-empty {
  margin: 0;
  padding: var(--space-8) var(--space-4);
  color: var(--ink-tertiary);
  font-size: var(--font-size-sm);
  text-align: center;
}

/* 单词表：表头和行共用同一套列宽 */
.word-table-head,
.word-row {
  display: grid;
  grid-template-columns:
    minmax(10rem, 1.3fr)
    minmax(6rem, 1fr)
    minmax(0, 1.6fr)
    3.5rem
    28px;
  align-items: center;
  column-gap: var(--space-4);
  padding: 0 var(--space-3) 0 var(--space-4);
}

.word-table-head {
  min-height: 32px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.word-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
}

.word-row {
  min-height: 44px;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  transition: background-color 160ms ease;
}

.word-row:hover {
  background: var(--surface-subtle);
}

.word-row.selected {
  background: var(--surface-active);
}

.word-row:focus-visible {
  outline: none;
  box-shadow: inset var(--focus-ring-brand-strong);
}

.col-word {
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  overflow: hidden;
  white-space: nowrap;
}

.word-text {
  flex: 0 0 auto;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  font-weight: 600;
}

.phonetic {
  min-width: 0;
  overflow: hidden;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.col-meaning,
.col-context {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.col-meaning {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
}

.col-context {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.col-date {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  text-align: right;
}

.row-audio {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-tertiary);
  cursor: pointer;
  opacity: 0;
  transition: opacity 160ms ease;
}

.word-row:hover .row-audio,
.word-row.selected .row-audio,
.row-audio:focus-visible {
  opacity: 1;
}

.row-audio:hover {
  background: var(--surface-panel);
  color: var(--signal-active-strong);
}

.row-audio:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.icon-sound {
  display: block;
  width: 14px;
  height: 14px;
  background: currentColor;
  mask-image: url('~@/assets/ui-icons/volume.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/volume.svg');
  mask-position: center;
  -webkit-mask-position: center;
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-size: contain;
}

/* 面板变窄时依次隐藏出处、日期列 */
@container (max-width: 720px) {
  .word-table-head,
  .word-row {
    grid-template-columns: minmax(9rem, 1.2fr) minmax(0, 1fr) 3.5rem 28px;
  }

  .col-context {
    display: none;
  }
}

@container (max-width: 480px) {
  .word-table-head,
  .word-row {
    grid-template-columns: minmax(8rem, 1fr) minmax(0, 1fr) 28px;
  }

  .col-date {
    display: none;
  }
}

/* 右侧单词卡 */
.word-detail-panel {
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: var(--space-6);
  border-left: 1px solid var(--border-subtle);
  scrollbar-width: thin;
}

.detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.detail-heading {
  min-width: 0;
}

.detail-word {
  display: block;
  overflow-wrap: anywhere;
  color: var(--ink-primary);
  font-size: var(--font-size-3xl);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.detail-sub {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-3);
  margin-top: var(--space-2);
}

.detail-sub .phonetic {
  font-size: var(--font-size-sm);
}

.detail-pos,
.detail-lemma {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.detail-pos {
  color: var(--signal-active-strong);
  font-weight: 600;
}

.detail-play {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
  cursor: pointer;
  transition: opacity 160ms ease;
}

.detail-play:hover {
  opacity: 0.85;
}

.detail-play:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.detail-play .icon-sound {
  width: 16px;
  height: 16px;
}

.detail-translation {
  margin: var(--space-5) 0 0;
  color: var(--ink-primary);
  font-size: var(--font-size-xl);
  font-weight: 600;
  line-height: 1.4;
}

.detail-context-meaning {
  margin: var(--space-2) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.detail-section {
  margin-top: var(--space-6);
}

.detail-section h3 {
  margin: 0 0 var(--space-2);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.context-quote {
  margin: 0;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--surface-subtle);
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.7;
}

.context-quote mark {
  padding: 0 2px;
  border-radius: 3px;
  background: color-mix(in srgb, var(--signal-marker) 55%, transparent);
  color: inherit;
  font-weight: 600;
}

.source-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  margin-top: var(--space-3);
}

.source-name {
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.open-source {
  flex: 0 0 auto;
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 var(--space-4);
  color: var(--color-text-inverse);
  background: var(--color-text);
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 500;
}

.open-source svg {
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 280ms var(--ease-spring);
}

.open-source:hover:not(:disabled) svg {
  transform: translateX(3px);
}

.open-source:disabled {
  cursor: progress;
  opacity: 0.6;
}

.open-source:focus-visible {
  outline: 2px solid var(--color-brand-700);
  outline-offset: 2px;
}

.action-error {
  margin: var(--space-2) 0 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.definitions-list {
  margin: 0;
  padding-left: 1.25rem;
  display: grid;
  gap: var(--space-2);
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.definitions-list li::marker {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.definition-pos {
  margin-right: var(--space-2);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.detail-footer {
  margin-top: auto;
  padding-top: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.detail-delete {
  padding: var(--space-1) var(--space-2);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-tertiary);
  font: inherit;
  font-size: var(--font-size-xs);
  cursor: pointer;
}

.detail-delete:hover:not(:disabled) {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.detail-delete:disabled {
  cursor: wait;
  opacity: 0.6;
}

.detail-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  color: var(--ink-tertiary);
  text-align: center;
}

.detail-empty-mark {
  color: var(--border-default);
  font-family: var(--font-family-display);
  font-size: 2.5rem;
  line-height: 1;
}

.detail-empty strong {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.detail-empty p {
  max-width: 16rem;
  margin: 0;
  font-size: var(--font-size-xs);
  line-height: 1.6;
}

/* 删除确认弹窗内容 */
.delete-message {
  margin: 0 0 var(--space-2);
  color: var(--ink-primary);
  font-size: var(--font-size-md);
}

.delete-warning {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.dialog-action {
  height: 36px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.dialog-action.secondary {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.dialog-action.danger {
  border: 1px solid var(--color-danger);
  background: var(--color-danger);
  color: var(--color-text-inverse);
}

.dialog-action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (prefers-reduced-motion: reduce) {
  .notebook-tabs::before,
  .open-source svg {
    transition: none;
  }
}

@media (hover: none) {
  .row-audio {
    opacity: 1;
  }
}

@media (max-width: 900px) {
  .vocabulary-panel {
    height: auto;
  }

  .vocabulary-content {
    grid-template-columns: 1fr;
  }

  .word-list {
    max-height: 360px;
  }

  .word-detail-panel {
    border-left: 0;
    border-top: 1px solid var(--border-subtle);
  }
}

@media (max-width: 680px) {
  .notebook-tabs {
    align-self: flex-start;
  }

  .vocabulary-search {
    max-width: none;
  }
}
</style>
